// Записи операций без сети: в магазине связь часто пропадает. Черновик сохраняется на телефоне и уходит,
// когда появится интернет. У каждой записи свой clientId — сервер не создаст дубль, если запрос дошёл,
// а ответ потерялся.
import type { DraftInput } from "@home/shared";
import { useSyncExternalStore } from "react";
import { ApiError, NetworkError, api } from "./api.ts";
import { newId } from "./sync/engine.ts";
import { userCacheKey } from "./telegram.ts";

export interface QueuedOps {
  id: string;
  drafts: DraftInput[];
  source: "manual" | "text";
  queuedAt: number;
}

export interface QueueStore {
  load(): QueuedOps[];
  save(queue: QueuedOps[]): void;
}

type Send = (drafts: DraftInput[], source: "manual" | "text") => Promise<unknown>;

/** Повтор отправки, пока в очереди что-то есть. */
const RETRY_MS = 20_000;

export class OpsQueue {
  private queue: QueuedOps[];
  private listeners = new Set<() => void>();
  private flushing: Promise<void> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly send: Send,
    private readonly store: QueueStore,
    /** Сервер отказал (неверные данные, нет прав) — запись снята с очереди. */
    private readonly onDropped: (err: ApiError) => void = () => undefined,
    /** Записи ушли на сервер — экраны пора перезагрузить. */
    private readonly onSent: () => void = () => undefined,
  ) {
    this.queue = store.load();
  }

  get size(): number {
    return this.queue.length;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Записать: сразу на сервер, без сети — в очередь. "queued" — ждёт сети. Отказ сервера по существу (4xx)
   * — бросает. Сбой шлюза (5xx) — тоже в очередь: сервер мог уже сохранить, а повтор по clientId дубля не даст.
   */
  async submit(drafts: DraftInput[], source: "manual" | "text" = "manual"): Promise<"sent" | "queued"> {
    const withIds = drafts.map((d) => ({ ...d, clientId: d.clientId ?? newId() }));
    try {
      await this.send(withIds, source);
      return "sent";
    } catch (err) {
      if (!(err instanceof NetworkError || (err instanceof ApiError && err.status >= 500))) throw err;
      this.update([...this.queue, { id: newId(), drafts: withIds, source, queuedAt: Date.now() }]);
      this.schedule();
      return "queued";
    }
  }

  /** Отправить очередь по порядку. Нет сети — остановиться и повторить позже. */
  flush(): Promise<void> {
    this.flushing ??= this.doFlush().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async doFlush(): Promise<void> {
    let sent = 0;
    while (this.queue.length > 0) {
      const head = this.queue[0]!;
      try {
        await this.send(head.drafts, head.source);
        sent++;
      } catch (err) {
        if (err instanceof NetworkError || (err instanceof ApiError && (err.status === 401 || err.status >= 500))) {
          // Сеть или сервер недоступны, вход не прошёл — запись подождёт.
          this.schedule();
          break;
        }
        // Сервер отказал по существу — повтор не поможет, а очередь не должна застрять.
        if (err instanceof ApiError) this.onDropped(err);
      }
      this.update(this.queue.filter((q) => q.id !== head.id));
    }
    if (sent > 0) this.onSent();
  }

  private schedule(): void {
    if (this.timer || this.queue.length === 0) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, RETRY_MS);
  }

  private update(next: QueuedOps[]): void {
    this.queue = next;
    this.store.save(next);
    for (const listener of this.listeners) listener();
  }
}

/** Очередь в localStorage — переживает закрытие приложения; своя у каждого пользователя. */
export function localQueueStore(): QueueStore {
  const key = () => `ops-outbox:${userCacheKey()}`;
  return {
    load() {
      try {
        return JSON.parse(localStorage.getItem(key()) ?? "[]") as QueuedOps[];
      } catch {
        return [];
      }
    },
    save(queue) {
      try {
        localStorage.setItem(key(), JSON.stringify(queue));
      } catch {
        // нет места или приватный режим — очередь останется в памяти до закрытия
      }
    },
  };
}

let shared: OpsQueue | null = null;

/** Очередь приложения: отправляет при старте, при появлении сети и при возвращении в приложение. */
export function initOpsQueue(onDropped: (err: ApiError) => void, onSent: () => void): OpsQueue {
  if (shared) return shared;
  const queue = new OpsQueue((drafts, source) => api.createTransactions(drafts, source), localQueueStore(), onDropped, onSent);
  shared = queue;
  window.addEventListener("online", () => void queue.flush());
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && void queue.flush());
  if (queue.size > 0) void queue.flush();
  return queue;
}

export function opsQueue(): OpsQueue {
  if (!shared) throw new Error("initOpsQueue() не вызван");
  return shared;
}

/** Сколько записей ждут отправки (для строки состояния). */
export function useOpsPending(): number {
  return useSyncExternalStore(
    (listener) => opsQueue().subscribe(listener),
    () => opsQueue().size,
  );
}
